import { useEffect, useRef } from "react";
import { trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

interface UseScreenWakeLockOptions {
    enabled?: boolean;
    diagnosticsEnabled?: boolean;
    area: string;
    context?: Record<string, any>;
}

interface WakeLockSentinelLike {
    released: boolean;
    release: () => Promise<void>;
    addEventListener: (type: "release", listener: () => void) => void;
    removeEventListener: (type: "release", listener: () => void) => void;
}

export const useScreenWakeLock = ({
    enabled,
    diagnosticsEnabled,
    area,
    context = {}
}: UseScreenWakeLockOptions): void => {
    const wakeLockRef = useRef<WakeLockSentinelLike>();
    const requestingRef = useRef(false);
    const contextRef = useRef(context);

    useEffect(() => {
        contextRef.current = context;
    }, [context]);

    useEffect(() => {
        if (!enabled) return;

        let disposed = false;

        const log = (event: string, properties: Record<string, any> = {}): void => {
            trackDiagnosticTrace({
                enabled: diagnosticsEnabled,
                area,
                event,
                properties: {
                    ...contextRef.current,
                    ...properties
                }
            });
        };

        const releaseWakeLock = async (): Promise<void> => {
            const wakeLock = wakeLockRef.current;
            wakeLockRef.current = undefined;

            if (!wakeLock || wakeLock.released) return;

            try {
                await wakeLock.release();
                log("wake-lock:released");
            } catch (er) {
                log("wake-lock:release-error", { er });
            }
        };

        const requestWakeLock = async (): Promise<void> => {
            if (requestingRef.current || wakeLockRef.current != null || document.visibilityState !== "visible") {
                return;
            }

            const wakeLockApi = (navigator as any).wakeLock;

            if (!wakeLockApi?.request) {
                log("wake-lock:unsupported");
                return;
            }

            requestingRef.current = true;

            try {
                const wakeLock = (await wakeLockApi.request("screen")) as WakeLockSentinelLike;

                if (disposed) {
                    await wakeLock.release();
                    return;
                }

                const handleRelease = (): void => {
                    wakeLock.removeEventListener("release", handleRelease);

                    if (wakeLockRef.current === wakeLock) {
                        wakeLockRef.current = undefined;
                    }

                    log("wake-lock:released-by-browser");
                };

                wakeLock.addEventListener("release", handleRelease);
                wakeLockRef.current = wakeLock;
                log("wake-lock:acquired");
            } catch (er) {
                log("wake-lock:request-error", { er });
            } finally {
                requestingRef.current = false;
            }
        };

        const handleVisibilityChange = (): void => {
            log("document:visibility-change", { visibilityState: document.visibilityState });

            if (document.visibilityState === "visible") {
                void requestWakeLock();
            }
        };

        const handlePageHide = (): void => {
            log("document:pagehide");
        };

        void requestWakeLock();
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            disposed = true;
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", handlePageHide);
            void releaseWakeLock();
        };
    }, [area, diagnosticsEnabled, enabled]);
};
